import React, { useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';

/* ─── ANIMATIONS ─────────────────────────────────────────────── */

const lineGrow = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const revealUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const revealLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const floatPulse = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
`;

const tickerSlide = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(55, 48, 163, 0.3); }
  50%       { box-shadow: 0 0 40px rgba(55, 48, 163, 0.6); }
`;

const staggerAnim = (delay) => css`
  animation: ${revealUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s both;
`;

/* ─── ROOT SECTION ───────────────────────────────────────────── */

const Section = styled.section`
  position: relative;
  width: 100%;
  height: 820px;
  background: #ffffff;
  overflow: hidden;
  margin-bottom: 60px;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    height: 680px;
    margin-bottom: 40px;
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: 0;
    margin-bottom: 24px;
  }
`;


/* ─── BACKGROUND ─────────────────────────────────────────────── */

const BgImage = styled.img`
  position: absolute;
  top: 70px;
  right: 8%;
  width: 52%;
  height: 670px;
  object-fit: contain;
  object-position: right center;
  z-index: 1;

  @media (max-width: 1366px) and (min-width: 769px) {
    top: 55px;
    right: 8%;
    width: 52%;
    height: 560px;             /* Same as FrostedPanel height */
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    top: 0;
    right: 0;
    object-fit: cover;
    object-position: center;
  }
`;

/* ─── LEFT FROSTED PANEL ─────────────────────────────────────── */

const FrostedPanel = styled.div`
  position: absolute;
  z-index: 3;
  top: 70px;
  left: 8%;
  width: 38%;
  height: 670px;
  background: linear-gradient(
    135deg,
    rgba(10, 10, 50, 0.92) 0%,
    rgba(20, 18, 80, 0.88) 60%,
    rgba(55, 48, 163, 0.75) 100%
  );
  backdrop-filter: blur(2px);
  clip-path: polygon(0 0, 92% 0, 100% 100%, 0 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 3rem 3rem 3rem 2.5rem;
  box-sizing: border-box;
  font-family: 'Times New Roman', serif;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    top: 55px;
    height: 560px;             /* Same as BgImage height */
    left: 8%;
    width: 38%;
    padding: 2.5rem 2.5rem 2.5rem 2rem;
  }

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    height: auto;
    clip-path: none;
    padding: 2rem 1.5rem 3rem;
    text-align: center;
  }
`;

/* ─── VERTICAL RULE ──────────────────────────────────────────── */

const VerticalRule = styled.div`
  position: absolute;
  left: 2.5rem;
  top: 3rem;
  bottom: 3rem;
  width: 1.5px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(196, 181, 253, 0.3) 20%,
    rgba(196, 181, 253, 0.4) 50%,
    rgba(196, 181, 253, 0.3) 80%,
    transparent 100%
  );
  animation: ${glowPulse} 3s ease-in-out infinite;

  @media (max-width: 1366px) and (min-width: 769px) {
    left: 2rem;
    top: 2rem;
    bottom: 2rem;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ─── INNER CONTENT ───────────────────────────────────────────── */

const InnerContent = styled.div`
  padding-left: 1rem;
  position: relative;
  z-index: 1;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding-left: 0.8rem;
  }

  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

/* ─── BADGE ──────────────────────────────────────────────────── */

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0.5rem 1.2rem;
  border-radius: 2px;
  background: rgba(167, 139, 250, 0.15);
  border-left: 3px solid #a78bfa;
  font-family: 'Times New Roman', serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #c4b5fd;
  margin-bottom: 2rem;
  ${staggerAnim(0.1)}

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.65rem;
    margin-bottom: 1.2rem;
    padding: 0.35rem 0.9rem;
  }

  @media (max-width: 768px) {
    font-size: 0.6rem;
    margin-bottom: 1rem;
    padding: 0.35rem 0.9rem;
    letter-spacing: 1.5px;
  }
`;

/* ─── TITLE ──────────────────────────────────────────────────── */

const Title = styled.h1`
  font-family: 'Times New Roman', serif;
  font-size: 3.6rem;
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: -0.5px;
  margin: 0 0 0.8rem 0;
  color: #ffffff;
  ${staggerAnim(0.2)}

  .accent {
    display: block;
    font-family: 'Times New Roman', serif;
    font-size: 1.4rem;
    font-weight: 400;
    font-style: italic;
    color: #c4b5fd;
    letter-spacing: 1px;
    margin-bottom: 0.4rem;
  }

  .highlight {
    display: block;
    font-family: 'Times New Roman', serif;
    font-size: 2.8rem;
    background: linear-gradient(90deg, #a78bfa, #7c3aed, #4f46e5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2.4rem;

    .accent { font-size: 1rem; }
    .highlight { font-size: 1.8rem; }
  }

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-bottom: 0.5rem;
    .accent { font-size: 1rem; margin-bottom: 0.2rem; }
    .highlight { font-size: 1.45rem; }
  }
`;

/* ─── HORIZONTAL LINE ────────────────────────────────────────── */

const HRule = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, rgba(167,139,250,0.5), transparent);
  margin: 1.8rem 0;
  transform-origin: left;
  animation: ${lineGrow} 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin: 1.2rem 0;
  }

  @media (max-width: 768px) {
    margin: 1rem 0;
    background: linear-gradient(90deg, transparent, rgba(167,139,250,0.5), transparent);
  }
`;

/* ─── DESCRIPTION ────────────────────────────────────────────── */

const Description = styled.div`
  font-family: 'Times New Roman', serif;
  font-size: 1.1rem;
  line-height: 1.9;
  color: rgba(220, 215, 255, 0.88);
  ${staggerAnim(0.35)}

  p { 
    margin-bottom: 1rem; 
    font-family: 'Times New Roman', serif;
  }
  p:last-child { margin-bottom: 0; }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.85rem;
    line-height: 1.7;
    
    p { margin-bottom: 0.7rem; }
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
    p { margin-bottom: 0.6rem; }
  }
`;

/* ─── STAT PILLS ─────────────────────────────────────────────── */

const StatRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;
  ${staggerAnim(0.45)}
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 0.8rem;
    margin: 1.3rem 0;
  }

  @media (max-width: 768px) {
    justify-content: center;
    gap: 0.6rem;
    margin: 1.2rem 0;
  }
`;

const StatPill = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.8rem 1.4rem;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(167, 139, 250, 0.3);
  border-radius: 4px;
  min-width: 80px;
  font-family: 'Times New Roman', serif;

  .num {
    font-family: 'Times New Roman', serif;
    font-size: 1.5rem;
    font-weight: 900;
    color: #a78bfa;
    line-height: 1;
  }

  .label {
    font-family: 'Times New Roman', serif;
    font-size: 0.65rem;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: rgba(200,190,255,0.65);
    margin-top: 5px;
    text-align: center;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.55rem 0.9rem;
    min-width: 65px;

    .num { font-size: 1.1rem; }
    .label { font-size: 0.52rem; }
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    min-width: 70px;
    .num { font-size: 1.2rem; }
    .label { font-size: 0.55rem; }
  }
`;

/* ─── CTA BUTTON ─────────────────────────────────────────────── */

const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0.95rem 2.2rem;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-family: 'Times New Roman', serif;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #1a1060;
  background: linear-gradient(135deg, #e2d9ff, #a78bfa);
  box-shadow: 0 8px 24px -6px rgba(55, 48, 163, 0.5);
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
  ${staggerAnim(0.55)}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #a78bfa, #7c3aed);
    opacity: 0;
    transition: opacity 0.35s ease;
  }

  span { 
    position: relative; 
    z-index: 1;
    font-family: 'Times New Roman', serif;
  }

  .arrow {
    font-family: 'Times New Roman', serif;
    font-size: 1.1rem;
    transition: transform 0.3s ease;
    position: relative;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 32px -6px rgba(55, 48, 163, 0.65);

    &::before { opacity: 1; }
    span { color: #fff; }
    .arrow { transform: translateX(5px); color: #fff; }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.78rem;
    padding: 0.7rem 1.6rem;
  }

  @media (max-width: 768px) {
    font-size: 0.78rem;
    padding: 0.8rem 1.8rem;
    margin-top: 0.4rem;
  }
`;

/* ─── FLOATING ACCENT TAG ────────────────────────────────────── */

const AccentTag = styled.div`
  position: absolute;
  top: 2.2rem;
  right: -1px;
  background: #3730A3;
  color: white;
  font-family: 'Times New Roman', serif;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 0.3rem 0.8rem;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  border-left: 2px solid #a78bfa;
  animation: ${revealLeft} 1s ease 0.7s both;

  @media (max-width: 1366px) and (min-width: 769px) {
    top: 1.5rem;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ─── TICKER BAR ─────────────────────────────────────────────── */

const TickerBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 8%;
  right: 8%;
  width: auto;
  z-index: 4;
  background: rgba(55, 48, 163, 0.95);
  border-top: 1px solid rgba(167, 139, 250, 0.4);
  overflow: hidden;
  height: 36px;
  display: flex;
  align-items: center;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    left: 8%;
    right: 8%;
    height: 30px;
  }

  @media (max-width: 768px) {
    left: 0;
    right: 0;
    width: 100%;
    bottom: 0;
  }
`;

const TickerTrack = styled.div`
  display: flex;
  gap: 0;
  white-space: nowrap;
  animation: ${tickerSlide} 35s linear infinite;
  font-family: 'Times New Roman', serif;
`;

const TickerItem = styled.span`
  font-family: 'Times New Roman', serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(220, 215, 255, 0.9);

  &::after {
    content: '◆';
    margin-left: 2.5rem;
    color: #a78bfa;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.55rem;
  }
`;

/* ─── CORNER DECORATION ──────────────────────────────────────── */

const CornerDeco = styled.div`
  position: absolute;
  bottom: 3rem;
  right: 2.5rem;
  width: 40px;
  height: 40px;
  border-bottom: 2px solid rgba(167, 139, 250, 0.5);
  border-right: 2px solid rgba(167, 139, 250, 0.5);
  animation: ${floatPulse} 4s ease-in-out infinite;

  @media (max-width: 1366px) and (min-width: 769px) {
    bottom: 2rem;
    right: 2rem;
    width: 30px;
    height: 30px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ─── COMPONENT ──────────────────────────────────────────────── */

const TICKER_ITEMS = [
  'CCS Certified Coding Specialist',
  'CDI Clinical Documentation Improvement',
  'Inpatient DRG Training',
  'HCC Risk Adjustment',
  'Healthcare Revenue Cycle',
  "India's #1 CDI Trainer",
  '98% Pass Rate',
  '5000+ Students',
];

export default function WelcomeSection() {
  const navigate = useNavigate();

  return (
    <Section>
      <BgImage src="/images/welcomebg.png" alt="Welcome Background" />

      <FrostedPanel>
        <VerticalRule />
        <AccentTag>Est. Academy</AccentTag>
        <CornerDeco />

        <InnerContent>
          <Badge>India's #1 CDI Trainer</Badge>

          <Title>
            <span className="accent">Master Your Career In</span>
            CCS &amp; CDI
            <span className="highlight">Certifications</span>
          </Title>

          <HRule />

          <Description>
            <p>
              At Learn Thru Us Training Academy, we specialize in
              CCS and CDI training with a proven
              98% certification pass rate.
            </p>
            <p>
              Led by Gaayatri Jayapal — India's first AHIMA-approved CDI
              trainer — shaping careers in Medical Coding,
              Inpatient DRG, HCC Risk Adjustment, and
              Healthcare Revenue Cycle Management.
            </p>
          </Description>

          <StatRow>
            <StatPill>
              <span className="num">98%</span>
              <span className="label">Pass Rate</span>
            </StatPill>
            <StatPill>
              <span className="num">5K+</span>
              <span className="label">Students</span>
            </StatPill>
            <StatPill>
              <span className="num">#1</span>
              <span className="label">CDI Trainer</span>
            </StatPill>
          </StatRow>

          <CTAButton onClick={() => navigate('/all-courses')}>
            <span>Explore Programs</span>
            <span className="arrow">→</span>
          </CTAButton>
        </InnerContent>
      </FrostedPanel>

      <TickerBar>
        <TickerTrack>
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <TickerItem key={i}>{item}</TickerItem>
          ))}
        </TickerTrack>
      </TickerBar>
    </Section>
  );
}